<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/menu';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {ActionButton} from '@react-spectrum/button'
import Copy from '@spectrum-icons/workflow/Copy';
import Cut from '@spectrum-icons/workflow/Cut';
import {Item, Menu, MenuTrigger, Section} from '@react-spectrum/menu';
import Paste from '@spectrum-icons/workflow/Paste';
```

# MenuTrigger

<p>{docs.exports.MenuTrigger.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['MenuTrigger']}
  sourceData={[
    {type: 'Spectrum', url: 'https://spectrum.adobe.com/page/popovers/'}
  ]} />

## Example

```tsx example
<MenuTrigger>
  <ActionButton>
      Menu Button
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

## Content

The MenuTrigger accepts exactly two children: the Menu and the element which
triggers the opening of the Menu. The trigger must be the first child passed into
the MenuTrigger and should be an element that supports press events.

If the Menu is open it will close on blur or scroll events.

The `defaultSelectedKeys` prop can be used to preselect items in the Menu
placing it in an uncontrolled state. Alternatively, the `selectedKeys` preselect
items in the Menu placing it in a controlled state.

```tsx example
<MenuTrigger closeOnSelect={false}>
  <ActionButton>
      Menu Controlled
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="single"
    selectedKeys={['Kangaroo']}>
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger closeOnSelect={false}>
  <ActionButton>
      Menu Uncontrolled
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="single"
    defaultSelectedKeys={['Snake']}>
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

The `selectionMode` prop can be used to switch from the default selection of a single menu item to multiple menu items or none.

```tsx example
<MenuTrigger closeOnSelect={false}>
  <ActionButton>
      Menu Selection Multiple
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    defaultSelectedKeys={['Kangaroo', 'Snake']}
    selectionMode="multiple">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger closeOnSelect={false}>
  <ActionButton>
      Menu Selection None
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="none">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Sections

Menus may have Sections with titles and children items.

```tsx example
<MenuTrigger>
  <ActionButton>
      Menu Button
  </ActionButton>
  <Menu items={[{name: 'Section 1', children: [{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}, {name: 'Section 2', children: [{name: 'Koala'}]}]} itemKey="name">
    {item => (
      <Section items={item.children} title={item.name}>
        {item => <Item>{item.name}</Item>}
      </Section>
    )}
  </Menu>
</MenuTrigger>
```

### Sematic Elements

A Menu item's content may be any renderable node, not just strings.

[View guidelines](https://spectrum.adobe.com/page/popovers/#Content-types)

```tsx example
import {Keyboard, Text} from '@react-spectrum/typography';

<MenuTrigger>
  <ActionButton>
      Menu Button
  </ActionButton>
  <Menu
    itemKey="name"
    items={[
      {name: 'Copy', icon: 'Copy', shortcut: '⌘C'},
      {name: 'Cut', icon: 'Cut', shortcut: '⌘X'},
      {name: 'Paste', icon: 'Paste', shortcut: '⌘V'}
    ]}>
    {item => {
      let iconMap = {
        Copy,
        Cut,
        Paste
      };
      let Icon = iconMap[item.icon];
      return (
        <Item childItems={item.children} textValue={item.name}>
          <Icon size="S" />
          <Text>{item.name}</Text>
          <Keyboard>{item.shortcut}</Keyboard>
        </Item>
      );
    }}
  </Menu>
</MenuTrigger>
```

### Accessibility

Menu sections without labels use the prop `aria-label`.

```tsx example
<MenuTrigger>
  <ActionButton>
      Menu Button
  </ActionButton>
  <Menu items={[{name: 'Section 1', children: [{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}, {name: 'Section 2', children: [{name: 'Koala'}]}]} itemKey="name">
    {item => (
      <Section items={item.children} aria-label={item.name}>
        {item => <Item>{item.name}</Item>}
      </Section>
    )}
  </Menu>
</MenuTrigger>
```

## Events

MenuTrigger accepts an `onOpenChange` handler which is triggered whenever the Menu is opened or closed.

The example below uses `onOpenChange` to update a separate span element with the current open state of the Menu.

```tsx example
function Example() {
  let [state, setState] = React.useState(false);

  return (
    <div>
      <MenuTrigger onOpenChange={(isOpen) => setState(isOpen)}>
        <ActionButton>
            Menu Button
        </ActionButton>
        <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
          {item => <Item>{item.name}</Item>}
        </Menu>
      </MenuTrigger>
      <span style={{'margin-left': '8px'}}>Current open state: {state.toString()}</span>
    </div>
  );
}
```

Menu accepts an `onAction` handler which is triggered whenever a Menu item is selected.

The example below uses `onAction` to tell the user which Menu item was selected.

```tsx example
function Example() {
  let onAction = (value) => alert('Menu item selected: ' + value);

  return (
    <div>
      <MenuTrigger>
        <ActionButton>
            Menu Button
        </ActionButton>
        <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name" onAction={onAction}>
          {item => <Item>{item.name}</Item>}
        </Menu>
      </MenuTrigger>
    </div>
  );
}
```

## Props

### MenuTrigger

<PropTable component={docs.exports.MenuTrigger} links={docs.links} />

### Menu

<PropTable component={docs.exports.Menu} links={docs.links} />

## Visual Options

### Align and Direction

[View guidelines](https://spectrum.adobe.com/page/popovers/#Placement)

```tsx example
<MenuTrigger align="start" direction="bottom" shouldFlip={false}>
  <ActionButton>
      align=start direction=bottom
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger align="start" direction="top" shouldFlip={false}>
  <ActionButton>
      align=start direction=top
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger align="end" direction="bottom" shouldFlip={false}>
  <ActionButton>
      align=end direction=bottom
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger align="end" direction="top" shouldFlip={false}>
  <ActionButton>
      align=end direction=top
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Autofocus

Applying `autoFocus` to the Menu of the MenuTrigger sets focus within the Menu upon opening.

```tsx example
<MenuTrigger>
  <ActionButton>
      autofocus
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="single"
    selectedKeys={['Kangaroo']}
    autoFocus>
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger>
  <ActionButton>
      autofocus=false
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="single"
    selectedKeys={['Kangaroo']}
    autoFocus={false}>
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger>
  <ActionButton>
      autofocus=first
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    autoFocus="first">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger>
  <ActionButton>
      autofocus=last
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    autoFocus="last">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Closes Menu onSelect

```tsx example
<MenuTrigger closeOnSelect>
  <ActionButton>
      closeOnSelect=true
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="single">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger closeOnSelect={false}>
  <ActionButton>
      closeOnSelect=false
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]}
    itemKey="name"
    selectionMode="single">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Disabled Trigger

```tsx example
<MenuTrigger>
  <ActionButton isDisabled>
      Disabled Menu Button
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Disabled Menu Items

```tsx example
<MenuTrigger>
  <ActionButton>
      Disabled Menu Items
  </ActionButton>
  <Menu
    items={[{name: 'Aardvark', dataId: 'a1b2c3'}, {name: 'Kangaroo', dataId: 'g5h1j9'}, {name: 'Snake', dataId: 'p8k3i4'}]}
    itemKey="dataId"
    disabledKeys={['a1b2c3', 'p8k3i4']}>
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Flipping

Applying `shouldFlip` to the MenuTrigger makes the Menu attempt to flip on its
main axis in situations where the original placement would cause it to render out of view.

```tsx example
<MenuTrigger shouldFlip>
  <ActionButton>
      shouldFlip=true
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
<MenuTrigger shouldFlip={false}>
  <ActionButton>
      shouldFlip=false
  </ActionButton>
  <Menu items={[{name: 'Aardvark'}, {name: 'Kangaroo'}, {name: 'Snake'}]} itemKey="name">
    {item => <Item>{item.name}</Item>}
  </Menu>
</MenuTrigger>
```

### Open

The `isOpen` and `defaultOpen` props control whether the MenuTrigger is open by default.
They apply controlled and uncontrolled behavior on the MenuTrigger respectively.
